import React, { useEffect, useState } from 'react';
import { Mail, Calendar, Star, Trash2, Plus, AlertCircle, CheckCircle, RefreshCw, Inbox } from 'lucide-react';
import axios from 'axios';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { connectedAccountsApi, ConnectedAccountDTO } from '../../services/connectedAccountsApi';

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

const ConnectedAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<ConnectedAccountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const { setShowUpgradeModal } = useSubscription();

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setShowUpgrade(false);
      const list = await connectedAccountsApi.list();
      setAccounts(list);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setShowUpgrade(true);
      } else {
        setError('Failed to load connected accounts');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startConnect = async (kind: 'gmail' | 'calendar') => {
    try {
      const endpoint = kind === 'gmail' ? '/api/email-auth/auth' : '/api/calendar-auth/authorize';
      const { data } = await axios.get<{ authUrl: string }>(endpoint);
      window.open(data.authUrl, `${kind}-auth`, 'width=600,height=700');

      // Poll for new accounts after the user finishes the OAuth flow.
      const stopAt = Date.now() + 2 * 60 * 1000;
      const tick = setInterval(async () => {
        try {
          const list = await connectedAccountsApi.list();
          setAccounts(list);
        } catch {
          /* ignore */
        }
        if (Date.now() > stopAt) clearInterval(tick);
      }, 2000);
    } catch (err) {
      console.error(err);
      setError('Failed to start authorization');
    }
  };

  const handleDisconnect = async (account: ConnectedAccountDTO) => {
    if (!window.confirm(`Disconnect ${account.email}? Reminders sent from this account will stop.`)) {
      return;
    }
    try {
      setBusyId(account.id);
      await connectedAccountsApi.disconnect(account.id);
      await load();
    } catch (err) {
      console.error(err);
      setError('Failed to disconnect account');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetPrimary = async (account: ConnectedAccountDTO, capability: 'send' | 'calendar') => {
    try {
      setBusyId(account.id);
      await connectedAccountsApi.setPrimary(account.id, capability);
      await load();
    } catch (err) {
      console.error(err);
      setError('Failed to set primary account');
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncNow = async (account: ConnectedAccountDTO) => {
    try {
      setBusyId(account.id);
      const result = await connectedAccountsApi.syncNow(account.id);
      await load();
      if (result.errors.length > 0) {
        setError(`Sync finished with errors: ${result.errors.join('; ')}`);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error || 'Failed to sync');
    } finally {
      setBusyId(null);
    }
  };

  // For accounts that have send-only Gmail today, re-running OAuth re-asks for both scopes
  // and the upsert merges them. Same endpoint as "Add Gmail account" — only the label differs.
  const handleEnableSync = async () => {
    try {
      const { data } = await axios.get<{ authUrl: string }>('/api/email-auth/auth');
      window.open(data.authUrl, 'gmail-enable-sync', 'width=600,height=700');
      const stopAt = Date.now() + 2 * 60 * 1000;
      const tick = setInterval(async () => {
        try {
          const list = await connectedAccountsApi.list();
          setAccounts(list);
        } catch {
          /* ignore */
        }
        if (Date.now() > stopAt) clearInterval(tick);
      }, 2000);
    } catch (err) {
      console.error(err);
      setError('Failed to start authorization');
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg border shadow-sm mb-6">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (showUpgrade) {
    return (
      <div className="bg-white border rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Connected Accounts</h2>
        <p className="text-gray-600 mb-4">
          Connecting Google accounts requires a Medium or Premium plan.
        </p>
        <button
          onClick={() => setShowUpgradeModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Upgrade Plan
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold">Connected Accounts</h2>
          <p className="text-sm text-gray-500 mt-1">
            Connect multiple Google accounts (e.g. business + personal). Inner Circle uses the primary
            account for sending email and calendar actions; passive interaction sync uses all of them.
          </p>
        </div>
      </div>

      {accounts.length === 0 ? (
        <div className="p-4 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 flex items-center mb-4">
          <AlertCircle size={20} className="mr-2" />
          No accounts connected yet.
        </div>
      ) : (
        <ul className="divide-y border rounded-lg mb-4">
          {accounts.map(account => (
            <li key={account.id} className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="font-medium truncate">{account.email}</div>
                {account.displayName && (
                  <div className="text-xs text-gray-500 truncate">{account.displayName}</div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {account.canSendEmail && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                      <Mail size={12} /> Send email
                      {account.isPrimaryForSend && <Star size={12} className="ml-0.5" fill="currentColor" />}
                    </span>
                  )}
                  {account.canUseCalendar && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-50 text-purple-700 border border-purple-200">
                      <Calendar size={12} /> Calendar
                      {account.isPrimaryForCalendar && <Star size={12} className="ml-0.5" fill="currentColor" />}
                    </span>
                  )}
                  {account.canReadEmail && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                      <Inbox size={12} /> Email sync
                    </span>
                  )}
                </div>
                {account.canReadEmail && (
                  <div className="text-xs text-gray-500 mt-2">
                    Last synced: {relativeTime(account.lastSyncedAt)}
                    {account.needsReauth ? (
                      <span className="ml-2 inline-flex items-center gap-1 text-red-600">
                        <AlertCircle size={12} /> Google revoked these permissions — email sync is
                        paused until you reconnect.
                      </span>
                    ) : (
                      account.lastSyncError && (
                        <span className="ml-2 inline-flex items-center gap-1 text-red-600">
                          <AlertCircle size={12} /> {account.lastSyncError}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 sm:justify-end">
                {account.needsReauth && (
                  <button
                    onClick={handleEnableSync}
                    disabled={busyId === account.id}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    title="Re-authorize this Google account to resume passive email sync"
                  >
                    <Inbox size={12} /> Reconnect
                  </button>
                )}
                {account.canSendEmail && !account.canReadEmail && !account.needsReauth && (
                  <button
                    onClick={handleEnableSync}
                    disabled={busyId === account.id}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    title="Re-authorize this Google account to enable passive email sync"
                  >
                    <Inbox size={12} /> Enable email sync
                  </button>
                )}
                {account.canReadEmail && !account.needsReauth && (
                  <button
                    onClick={() => handleSyncNow(account)}
                    disabled={busyId === account.id}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={busyId === account.id ? 'animate-spin' : ''} />
                    Sync now
                  </button>
                )}
                {account.canSendEmail && !account.isPrimaryForSend && (
                  <button
                    onClick={() => handleSetPrimary(account, 'send')}
                    disabled={busyId === account.id}
                    className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Make primary for sending
                  </button>
                )}
                {account.canUseCalendar && !account.isPrimaryForCalendar && (
                  <button
                    onClick={() => handleSetPrimary(account, 'calendar')}
                    disabled={busyId === account.id}
                    className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Make primary for calendar
                  </button>
                )}
                <button
                  onClick={() => handleDisconnect(account)}
                  disabled={busyId === account.id}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 size={12} /> Disconnect
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => startConnect('gmail')}
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded-lg"
        >
          <Plus size={14} /> Add Gmail account
        </button>
        <button
          onClick={() => startConnect('calendar')}
          className="inline-flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-1.5 rounded-lg"
        >
          <Plus size={14} /> Add Google Calendar
        </button>
      </div>

      {error && <p className="text-red-500 mt-3 text-center">{error}</p>}
    </div>
  );
};

export default ConnectedAccounts;
